/**
 * RicercaMenuView view component.
 * @module components/View/RicercaMenuView
 */

import {
  getUser,
  getVocabulary,
  resetContent,
  searchContent,
} from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import jwtDecode from 'jwt-decode';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  Dimmer,
  Form,
  Icon,
  Input,
  Loader,
  Pagination,
} from 'semantic-ui-react';
import { getExportedMenus } from '../../actions';
import {
  hasValue,
  seededOrder,
  tokenMatch,
  uniqueOptions,
} from '../../helpers/SearchHelper';
import useSearchFilterState from '../../hooks/useSearchFilterState';
import TitleStandard from '../Commons/TitleStandard';
import CardSearch from './Commons/CardSearch';
import TextBlocks from './Commons/RichTextBlocks/TextBlocks';

const MAX_ITEMS = 6;

const VOCAB_CUCINA = 'baccanale.policy.vocabulary.tipologia_cucina';
const VOCAB_PREZZO = 'baccanale.policy.vocabulary.fascia_prezzo';
const VOCAB_CONSEGNA = 'baccanale.policy.vocabulary.tipologia_consegna';
const VOCAB_TIPOLOGIA = 'baccanale.policy.vocabulary.tipologia_ristorante';

const SECTION_COMUNI = 0;
const SECTION_CUCINA = 1;
const SECTION_PREZZO = 2;
const SECTION_CONSEGNA = 3;
const SECTION_TIPOLOGIA = 4;

const PARAM_MAP = {
  comuni: { key: 'comuni', kind: 'list' },
  cucina: { key: 'cucina', kind: 'list' },
  prezzo: { key: 'prezzo', kind: 'list' },
  consegna: { key: 'consegna', kind: 'list' },
  tipologia: { key: 'tipologia', kind: 'list' },
  nome: { key: 'nome', kind: 'text' },
};

const ACCORDION_FOR_FILTER = {
  comuni: SECTION_COMUNI,
  cucina: SECTION_CUCINA,
  prezzo: SECTION_PREZZO,
  consegna: SECTION_CONSEGNA,
  tipologia: SECTION_TIPOLOGIA,
};

// This view lists ordinary menus; the "fuori menu" ones belong to
// RicercaFuorimenuView. Applied to every combination of filters, not just some.
const isMenu = (menu) => menu?.tipologia_menu_unico?.length === 0;

const PREDICATES = {
  comuni: (menu, values) => tokenMatch(menu?.ristorante?.comune, values),
  cucina: (menu, values) => tokenMatch(menu?.tipologia_cucina, values),
  prezzo: (menu, values) => tokenMatch(menu?.fascia_prezzo, values),
  consegna: (menu, values) => tokenMatch(menu?.tipologia_consegna, values),
  tipologia: (menu, values) =>
    tokenMatch(menu?.ristorante?.tipologia_ristorante, values),
  nome: (menu, text) =>
    (menu?.ristorante?.title || '').toLowerCase().includes(text.toLowerCase()),
};

export const applyFilters = (items, filter) =>
  (items || []).filter(
    (menu) =>
      isMenu(menu) &&
      Object.entries(filter).every(
        ([key, value]) =>
          !hasValue(value) || !PREDICATES[key] || PREDICATES[key](menu, value),
      ),
  );

const getColor = (type) => {
  switch (type) {
    case 'ristoranti-e-osterie':
      return 'yellow';
    case 'caffetterie-gastronomie':
      return 'green';
    case 'agriturismi':
      return 'red';
    default:
      return undefined;
  }
};

/**
 * RicercaMenuView view component class.
 * @function RicercaMenuView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const RicercaMenuView = ({ content }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const vocabularies = useSelector((state) => state?.vocabularies);
  const requestMenus = useSelector(
    (state) => state?.search?.subrequests?.menus,
  );
  const token = useSelector((state) => state?.userSession?.token);
  const users = useSelector((state) => state?.users);
  const menus = requestMenus?.items;

  const userId = token ? jwtDecode(token).sub : null;

  const {
    filter,
    page,
    seed,
    activeIndex,
    setValue,
    setText,
    setPage,
    toggleAccordion,
    isChecked,
    getText,
  } = useSearchFilterState({
    storageKey: 'ricerca_menu',
    paramMap: PARAM_MAP,
    accordionForFilter: ACCORDION_FOR_FILTER,
  });

  useEffect(() => {
    if (!users?.get?.loading && userId) {
      dispatch(getUser(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_CUCINA }));
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_PREZZO }));
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_CONSEGNA }));
    dispatch(getVocabulary({ vocabNameOrURL: VOCAB_TIPOLOGIA }));
    dispatch(
      searchContent(
        flattenToAppURL(content['id']),
        {
          portal_type: ['Menu'],
          review_state: 'published',
          fullobjects: 1,
          b_size: 10000,
        },
        'menus',
      ),
    );

    return () => {
      dispatch(resetContent('tipologia_cucina'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, content['@id']]);

  // Derived from the unfiltered result set, so the list of comuni does not
  // collapse as the user narrows the other filters.
  const comuniOptions = useMemo(
    () => uniqueOptions(menus, (menu) => menu?.ristorante?.comune),
    [menus],
  );

  // Order once, on the whole set, before filtering: that way the filtered list is
  // always a slice of one stable permutation, so the same seed and filters bring
  // back the same page of cards.
  const ordered = useMemo(() => seededOrder(menus, seed), [menus, seed]);
  const results = useMemo(() => applyFilters(ordered, filter), [
    ordered,
    filter,
  ]);

  const totalPages = Math.max(1, Math.ceil(results.length / MAX_ITEMS));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paged = useMemo(
    () => results.slice((safePage - 1) * MAX_ITEMS, safePage * MAX_ITEMS),
    [results, safePage],
  );

  const downloadFile = () => {
    dispatch(getExportedMenus(pathname));
  };

  return (
    <>
      <TitleStandard title={content.title} />

      <div className="menu-view ui container ricerca-menu">
        <div
          className={`printPDF ${
            !users?.user?.roles?.includes('Manager') ? 'hidden' : ''
          }`}
        >
          <button className="printButton" onClick={downloadFile}>
            Export RTF
          </button>
        </div>
        <div className="mt-2">
          <TextBlocks content={content} />
        </div>
        <div className="flex mt-5 mb-5 flex-wrap wrapper">
          <div style={{ flex: 1 }}>
            <Accordion fluid styled>
              <Accordion.Title
                active={activeIndex.includes(SECTION_COMUNI)}
                index={SECTION_COMUNI}
                onClick={(e, { index }) => toggleAccordion(index)}
              >
                <Icon name="dropdown" />
                COMUNE
              </Accordion.Title>
              <Accordion.Content active={activeIndex.includes(SECTION_COMUNI)}>
                {comuniOptions.map((i, index) => (
                  <Form.Checkbox
                    key={index}
                    label={i}
                    name="comuni"
                    value={i}
                    checked={isChecked('comuni', i)}
                    onChange={(e, { checked }) => {
                      setValue('comuni', i, checked);
                    }}
                  />
                ))}
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex.includes(SECTION_CUCINA)}
                index={SECTION_CUCINA}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                COSA VUOI MANGIARE?
              </Accordion.Title>
              <Accordion.Content active={activeIndex.includes(SECTION_CUCINA)}>
                <Form>
                  <Form.Group grouped>
                    {vocabularies?.[VOCAB_CUCINA]?.items?.map((i, index) => (
                      <Form.Checkbox
                        key={index}
                        label={i.label}
                        name={VOCAB_CUCINA}
                        value={i.value}
                        checked={isChecked('cucina', i.value)}
                        onChange={(e, { checked }) => {
                          setValue('cucina', i.value, checked);
                        }}
                      />
                    ))}
                  </Form.Group>
                </Form>
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex.includes(SECTION_PREZZO)}
                index={SECTION_PREZZO}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                FASCIA DI PREZZO
              </Accordion.Title>
              <Accordion.Content active={activeIndex.includes(SECTION_PREZZO)}>
                <Form>
                  <Form.Group grouped>
                    {vocabularies?.[VOCAB_PREZZO]?.items?.map((i, index) => (
                      <Form.Checkbox
                        key={index}
                        label={i.label}
                        name={VOCAB_PREZZO}
                        value={i.value}
                        checked={isChecked('prezzo', i.value)}
                        onChange={(e, { checked }) => {
                          setValue('prezzo', i.value, checked);
                        }}
                      />
                    ))}
                  </Form.Group>
                </Form>
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex.includes(SECTION_CONSEGNA)}
                index={SECTION_CONSEGNA}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                CONSEGNA/ASPORTO
              </Accordion.Title>
              <Accordion.Content
                active={activeIndex.includes(SECTION_CONSEGNA)}
              >
                <Form>
                  <Form.Group grouped>
                    {vocabularies?.[VOCAB_CONSEGNA]?.items?.map((i, index) => (
                      <Form.Checkbox
                        key={index}
                        label={i.label}
                        name={VOCAB_CONSEGNA}
                        value={i.value}
                        checked={isChecked('consegna', i.value)}
                        onChange={(e, { checked }) => {
                          setValue('consegna', i.value, checked);
                        }}
                      />
                    ))}
                  </Form.Group>
                </Form>
              </Accordion.Content>

              <Accordion.Title
                active={activeIndex.includes(SECTION_TIPOLOGIA)}
                index={SECTION_TIPOLOGIA}
                onClick={(e, { index }) => toggleAccordion(index)}
                className="mt-05"
              >
                <Icon name="dropdown" />
                TIPOLOGIA LOCALE
              </Accordion.Title>
              <Accordion.Content
                active={activeIndex.includes(SECTION_TIPOLOGIA)}
              >
                <Form>
                  <Form.Group grouped>
                    {vocabularies?.[VOCAB_TIPOLOGIA]?.items?.map((i, index) => (
                      <Form.Checkbox
                        key={index}
                        label={i.label}
                        name={VOCAB_TIPOLOGIA}
                        value={i.value}
                        checked={isChecked('tipologia', i.value)}
                        onChange={(e, { checked }) => {
                          setValue('tipologia', i.value, checked);
                        }}
                      />
                    ))}
                  </Form.Group>
                </Form>
              </Accordion.Content>
            </Accordion>
            <div className="mt-2">
              Oppure cerca per nome...
              <Input
                icon="search"
                placeholder="Nome del ristorante"
                className="w-100 mt-05"
                value={getText('nome')}
                onChange={(e, { value }) => {
                  setText('nome', value);
                }}
              />
            </div>
          </div>
          {requestMenus && (
            <div className="flex justify-between flex-wrap cards ">
              <Dimmer active={requestMenus?.loading && !menus?.length} inverted>
                <Loader
                  active={requestMenus?.loading}
                  size={'medium'}
                  inline="centered"
                >
                  Caricamento ...
                </Loader>
              </Dimmer>
              {paged.map((m, index) => (
                <CardSearch
                  key={index}
                  anno={m.anno}
                  titolo={m.title}
                  titolo2={m.ristorante?.title}
                  text={m.ristorante?.tipologia_ristorante?.title}
                  linkTo={m['@id']}
                  badgeColor={getColor(
                    m.ristorante?.tipologia_ristorante?.token,
                  )}
                />
              ))}

              {totalPages > 1 && (
                <Pagination
                  activePage={safePage}
                  siblingRange={0}
                  firstItem={null}
                  lastItem={null}
                  prevItem={undefined}
                  nextItem={undefined}
                  ellipsisItem="-"
                  totalPages={totalPages}
                  onPageChange={(e, data) => {
                    window.scrollTo(0, 400);
                    setPage(data.activePage);
                  }}
                  className="pagination w-100 flex justify-center"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RicercaMenuView;
